// NotificationContext.jsx
import React, { createContext, useState, useEffect, useContext } from "react";
import axios from "axios";
import { 
  getUserNotificationsURL, 
  markNotificationAsReadURL, 
  getGeneralNotificationsURL,
  conversationsURL 
} from "../../api/api";
import { useAuthContext } from "../";

export const NotificationContext = createContext();

const NotificationContextProvider = ({ children }) => {
  const [userNotifications, setUserNotifications] = useState([]);
  const [generalNotifications, setGeneralNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [hasUnreadGeneralNotifications, setHasUnreadGeneralNotifications] = useState(false);
  // State for unread chat messages.
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const { token, user } = useAuthContext();
  const [loading, setLoading] = useState(true);

  // Fetch general notifications (no token required)
  // const fetchGeneralNotifications = async (retries = 3) => {
  //   for (let attempt = 1; attempt <= retries; attempt++) {
  //     try {
  //       const response = await axios.get(getGeneralNotificationsURL);
  //       const general = response.data;
  //       setGeneralNotifications(general);
  //       localStorage.setItem("generalNotif", JSON.stringify(general));
  
  //       // Check for unread general notifications.
  //       const generalUnread = general.some((notif) => !notif.is_read);
  //       setHasUnreadGeneralNotifications(generalUnread);
  
  //       return; // Exit function on success
  //     } catch (error) {
  //       if (attempt < retries) {
  //         await new Promise((resolve) => setTimeout(resolve, 2000));
  //         continue;
  //       }
  //       console.error("Error fetching general notifications:", error);
  //     }
  //   }
  // };
  
  // Fetch user-specific notifications (requires token)
  const fetchUserNotifications = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(getUserNotificationsURL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const userSpecific = response.data.filter((notif) => notif.email !== null);
      const general = response.data.filter((notif) => notif.email === null);
  
      setUserNotifications(userSpecific);
      setGeneralNotifications(general);
      localStorage.setItem("userNotif", JSON.stringify(userSpecific));
      localStorage.setItem("generalNotif", JSON.stringify(general));
  
      const unreadUserCount = userSpecific.filter((notif) => !notif.is_read).length;
      const generalUnread = general.some((notif) => !notif.is_read);
  
      setNotificationCount(unreadUserCount);
      setHasUnreadGeneralNotifications(generalUnread);
    } catch (error) {
      console.error("Error fetching user notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch unread chat messages using the existing conversations endpoint.
  const fetchChatUnreadCount = async () => {
    if (!token || !user) return;
    try {
      // Assuming your endpoint is structured as /conversations/:userId
      const response = await axios.get(`${conversationsURL}/${user.user_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const conversations = response.data; // Adjust if your response is wrapped in an object.
  
      let totalUnread = 0;
      conversations.forEach((conversation) => {
        if (conversation.messages && Array.isArray(conversation.messages)) {
          conversation.messages.forEach((msg) => {
            // Count message as unread if it has not been read and the sender is not the current user.
            if (!msg.read && msg.sender !== user.user_id) {
              totalUnread += 1;
            }
          });
        }
      });
  
      setChatUnreadCount(totalUnread);
    } catch (error) {
      console.error("Error fetching chat unread count:", error);
    }
  };

  // Initial fetch of all notifications and chat unread count.
  useEffect(() => {
    const fetchAllNotifications = async () => {
      setLoading(true);
      try {
        //await fetchGeneralNotifications();
        if (token) {
          //await fetchUserNotifications();
          await fetchChatUnreadCount();
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchAllNotifications();
  }, [token, user]);

  // Polling: Refresh chat unread count every 5 seconds
  useEffect(() => {
    if (token && user) {
      const interval = setInterval(() => {
        fetchChatUnreadCount();
      }, 5000); // Adjust the interval as needed
      
      return () => clearInterval(interval);
    }
  }, [token, user]);

  // Function to mark a specific notification as read.
  const markNotificationAsRead = (notification_id) => {
    const updatedUserNotifications = userNotifications.map((notif) =>
      notif.notification_id === notification_id ? { ...notif, is_read: true } : notif
    );
    setUserNotifications(updatedUserNotifications);
    localStorage.setItem("userNotif", JSON.stringify(updatedUserNotifications));
    const unreadCount = updatedUserNotifications.filter((notif) => !notif.is_read).length;
    setNotificationCount(unreadCount);
  
    axios.put(markNotificationAsReadURL, { notification_id }, { headers: { Authorization: `Bearer ${token}` } })
      .catch((error) => console.error("Error marking notification as read:", error));
  };

  return (
    <NotificationContext.Provider
      value={{
        userNotifications,
        generalNotifications,
        notificationCount,
        hasUnreadGeneralNotifications,
        chatUnreadCount, // Expose unread chat messages count
        markNotificationAsRead,
        fetchChatUnreadCount, // Expose fetch function (if needed elsewhere)
        loading,
        setNotificationCount,
        setHasUnreadGeneralNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => useContext(NotificationContext);

export default NotificationContextProvider;
